import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { PriorityHighIcon, PriorityMediumIcon, PriorityLowIcon } from './Icons';

/**
 * Badge - Status indicator and label component
 * Perfect for showing document status, priority levels, etc.
 */
const Badge = ({ 
  variant = 'default', // 'default', 'secondary', 'success', 'warning', 'danger', 'outline'
  size = 'default', // 'sm', 'default', 'lg'
  children,
  icon,
  removable = false,
  onRemove,
  className = '',
  ...props 
}) => {
  const { theme } = useTheme();

  // Size variants
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    default: 'px-2.5 py-1 text-sm',
    lg: 'px-3 py-1.5 text-base'
  };

  // Variant styles
  const getVariantClasses = () => {
    const baseClasses = 'inline-flex items-center gap-1.5 font-medium rounded-full transition-all duration-200';
    
    switch (variant) {
      case 'secondary':
        return `${baseClasses} bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300`;
      
      case 'success':
        return `${baseClasses} bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800`;
      
      case 'warning':
        return `${baseClasses} bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-800`;
      
      case 'danger':
        return `${baseClasses} bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800`;
      
      case 'outline':
        return `${baseClasses} border border-gray-300 dark:border-gray-600 ${theme.colors.surface} ${theme.text.primary} hover:bg-gray-50 dark:hover:bg-gray-800`;
      
      default: // 'default'
        return `${baseClasses} bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800`;
    }
  };

  return (
    <span
      className={`
        ${getVariantClasses()}
        ${sizeClasses[size] || sizeClasses.default}
        ${className}
      `}
      {...props}
    >
      {/* Optional icon */}
      {icon && (
        <span className="flex-shrink-0">
          {icon}
        </span>
      )}
      
      {/* Content */}
      <span>{children}</span>
      
      {/* Optional remove button */}
      {removable && onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="flex-shrink-0 ml-1 hover:bg-black/10 dark:hover:bg-white/10 rounded-full p-0.5 transition-colors"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </span>
  );
};

/**
 * StatusBadge - Specialized badge for status indicators
 */
const StatusBadge = ({ status, className = '', ...props }) => {
  const getStatusVariant = (status) => {
    const statusLower = status?.toLowerCase();
    
    switch (statusLower) {
      case 'active':
      case 'published':
      case 'approved':
      case 'completed':
      case 'success':
        return 'success';
      
      case 'pending':
      case 'draft':
      case 'in-review':
      case 'warning':
        return 'warning';
      
      case 'inactive':
      case 'deprecated':
      case 'rejected':
      case 'failed':
      case 'error':
        return 'danger';
      
      case 'archived':
      case 'disabled':
        return 'secondary';
      
      default:
        return 'default';
    }
  };

  return (
    <Badge 
      variant={getStatusVariant(status)} 
      className={className} 
      {...props}
    >
      {status}
    </Badge>
  );
};

/**
 * PriorityBadge - Specialized badge for priority levels
 */
const PriorityBadge = ({ priority, className = '', ...props }) => {
  const getPriorityVariant = (priority) => {
    const priorityLower = priority?.toLowerCase();
    
    switch (priorityLower) {
      case 'high':
      case 'critical':
      case 'urgent':
        return 'danger';
      
      case 'medium':
      case 'normal':
        return 'warning';
      
      case 'low':
        return 'success';
      
      default:
        return 'default';
    }
  };

  const getPriorityIcon = (priority) => {
    const priorityLower = priority?.toLowerCase();
    
    switch (priorityLower) {
      case 'high':
      case 'critical':
      case 'urgent':
        return <PriorityHighIcon className="w-3 h-3" />;
      
      case 'medium':
      case 'normal':
        return <PriorityMediumIcon className="w-3 h-3" />;
      
      case 'low':
        return <PriorityLowIcon className="w-3 h-3" />;
      
      default:
        return null;
    }
  };

  return (
    <Badge 
      variant={getPriorityVariant(priority)} 
      icon={getPriorityIcon(priority)}
      className={className} 
      {...props}
    >
      {priority}
    </Badge>
  );
};

export { Badge, StatusBadge, PriorityBadge };
export default Badge;
